export interface DecodeRequest<T> {
  generation: number
  decode: () => Promise<T>
}

interface PendingDecode<T> extends DecodeRequest<T> {
  resolve: (value: T | null) => void
  reject: (reason: unknown) => void
}

/**
 * Native image decoding cannot be cancelled once started. Keep that unavoidable
 * decode bounded to one operation and retain only the newest queued request.
 */
export class LatestDecodeQueue<T> {
  private running = false
  private pending: PendingDecode<T> | null = null

  enqueue(request: DecodeRequest<T>): Promise<T | null> {
    return new Promise((resolve, reject) => {
      this.pending?.resolve(null)
      this.pending = { ...request, resolve, reject }
      void this.drain()
    })
  }

  cancelPending(): void {
    this.pending?.resolve(null)
    this.pending = null
  }

  private async drain(): Promise<void> {
    if (this.running) return
    this.running = true
    try {
      while (this.pending) {
        const request = this.pending
        this.pending = null
        try {
          request.resolve(await request.decode())
        } catch (error) {
          request.reject(error)
        }
      }
    } finally {
      this.running = false
      if (this.pending) void this.drain()
    }
  }
}
